import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Alert } from '../../components/Alert';
import { AppShell } from '../../components/AppShell';
import { Button } from '../../components/Button';
import { DeleteAccountDialog } from '../../components/DeleteAccountDialog';
import { Field } from '../../components/Field';
import { Section } from '../../components/Section';
import { SettingsNav } from '../../components/SettingsNav';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { messageForError } from '../../lib/errors';
import { changePasswordFormSchema, parseForm, type FieldErrors } from '../../lib/forms';

export function ProfilePage() {
  const { auth, refresh } = useAuth();
  const user = auth.status === 'authed' ? auth.user : null;
  const [pwErrors, setPwErrors] = useState<
    FieldErrors<'currentPassword' | 'newPassword' | 'confirm'>
  >({});
  const [deleteOpen, setDeleteOpen] = useState(false);

  const saveName = useMutation({
    mutationFn: (displayName: string) => api.auth.updateProfile({ displayName }),
    onSuccess: () => refresh(),
  });
  const changePassword = useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      api.auth.changePassword(input),
  });

  function onSaveName(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const v = String(new FormData(e.currentTarget).get('displayName') ?? '').trim();
    if (v) saveName.mutate(v);
  }
  function onChangePassword(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const r = parseForm(changePasswordFormSchema, form);
    if (r.errors) return setPwErrors(r.errors as typeof pwErrors);
    setPwErrors({});
    changePassword.mutate(
      { currentPassword: r.data.currentPassword, newPassword: r.data.newPassword },
      { onSuccess: () => form.reset() },
    );
  }

  return (
    <AppShell title="Profile">
      <SettingsNav />
      <div className="flex flex-col gap-4">
        <Section title="Your name">
          <form onSubmit={onSaveName} className="flex flex-col gap-3">
            <Field
              id="displayName"
              name="displayName"
              label="Display name"
              defaultValue={user?.displayName ?? ''}
              autoComplete="name"
              required
              maxLength={80}
            />
            {saveName.isError ? (
              <Alert tone="error">{messageForError(saveName.error)}</Alert>
            ) : null}
            {saveName.isSuccess ? <Alert tone="success">Saved.</Alert> : null}
            <Button type="submit" disabled={saveName.isPending}>
              Save
            </Button>
          </form>
        </Section>
        <Section title="Email" description={user?.email}>
          <p className="text-sm text-muted">
            {user?.emailVerifiedAt ? 'Verified.' : 'Not verified yet.'} Changing your email address
            is not supported yet.
          </p>
        </Section>
        <Section title="Change password">
          <form onSubmit={onChangePassword} noValidate className="flex flex-col gap-3">
            <Field
              id="currentPassword"
              name="currentPassword"
              label="Current password"
              type="password"
              autoComplete="current-password"
              required
              error={pwErrors.currentPassword}
            />
            <Field
              id="newPassword"
              name="newPassword"
              label="New password"
              type="password"
              autoComplete="new-password"
              required
              minLength={10}
              error={pwErrors.newPassword}
            />
            <Field
              id="confirm"
              name="confirm"
              label="Confirm new password"
              type="password"
              autoComplete="new-password"
              required
              error={pwErrors.confirm}
            />
            {changePassword.isError ? (
              <Alert tone="error">{messageForError(changePassword.error)}</Alert>
            ) : null}
            {changePassword.isSuccess ? (
              <Alert tone="success">
                Password changed. Other devices stay signed in until you revoke them.
              </Alert>
            ) : null}
            <Button type="submit" disabled={changePassword.isPending}>
              Change password
            </Button>
          </form>
        </Section>
        <Section
          title="Delete account"
          description="Removes your account and signs you out everywhere. Shared price and location observations stay, anonymised."
        >
          <Button variant="danger" onClick={() => setDeleteOpen(true)}>
            Delete my account…
          </Button>
        </Section>
      </div>
      <DeleteAccountDialog open={deleteOpen} onClose={() => setDeleteOpen(false)} />
    </AppShell>
  );
}
